import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { ReportReason } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../lib/useAsync'
import { number, priorityClass } from '../lib/format'
import { EmptyState, ErrorState, PageHeader, TableSkeleton } from '../components/ui'
import './Queue.css'

/** Screen 7b · Admin · Şikayət növbəsi. */

type Bucket = 'PENDING' | 'MINE' | 'RESOLVED' | 'APPEALED'

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'PENDING', label: 'Gözləyən' },
  { key: 'MINE', label: 'Mənə təyin olunan' },
  { key: 'RESOLVED', label: 'Bitmiş' },
  { key: 'APPEALED', label: 'Etiraz' },
]

const REASONS: { key: ReportReason | 'all'; label: string }[] = [
  { key: 'all', label: 'Növ: hamısı' },
  { key: 'ABUSE', label: 'Təhqiredici dil' },
  { key: 'PERSONAL_INFO', label: 'Şəxsi məlumat' },
  { key: 'SPAM', label: 'Spam' },
  { key: 'FAKE', label: 'Saxta vəziyyət' },
  { key: 'OTHER', label: 'Digər' },
]

const PAGE_SIZE = 25

export function Queue() {
  const navigate = useNavigate()
  const { can } = useAuth()

  const [bucket, setBucket] = useState<Bucket>('PENDING')
  const [reason, setReason] = useState<ReportReason | 'all'>('all')
  const [page, setPage] = useState(0)
  const [claiming, setClaiming] = useState(false)

  const { data, error, loading, reload } = useAsync(
    () => api.queue({ bucket, reason, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    [bucket, reason, page],
  )

  async function claim() {
    setClaiming(true)
    try {
      await api.claimQueue(5)
      setBucket('MINE')
      setPage(0)
      reload()
    } finally {
      setClaiming(false)
    }
  }

  const rows = data?.items ?? []
  const total = data?.total ?? 0
  const urgent = rows.filter((r) => r.priority === 'URGENT').length

  return (
    <>
      <PageHeader
        title="Şikayət növbəsi"
        subtitle={
          loading
            ? 'Yüklənir…'
            : `${number(total)} ${bucket === 'PENDING' ? 'gözləyir' : 'qeyd'}${
                urgent > 0 ? ` · ${number(urgent)} təcili` : ''
              }`
        }
        actions={
          <>
            <select
              className="select queue__filter"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value as ReportReason | 'all')
                setPage(0)
              }}
              aria-label="Şikayət növü"
            >
              {REASONS.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
            {can('MODERATOR') && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => void claim()}
                disabled={claiming}
              >
                {claiming ? 'Təyin edilir…' : 'Mənə təyin et'}
              </button>
            )}
          </>
        }
      />

      <div className="queue__tabs" role="tablist" aria-label="Növbə bölmələri">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            role="tab"
            aria-selected={bucket === b.key}
            className={`chip${bucket === b.key ? ' chip--active' : ''}`}
            onClick={() => {
              setBucket(b.key)
              setPage(0)
            }}
          >
            {b.label}
            {bucket === b.key && total > 0 && (
              <span className="chip__count">{number(total)}</span>
            )}
          </button>
        ))}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading ? (
        <TableSkeleton rows={7} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bu bölmədə şikayət yoxdur"
          description={
            bucket === 'PENDING'
              ? 'Növbə təmizdir — gözləyən şikayət qalmadı.'
              : 'Filtri dəyişib yenidən yoxlaya bilərsən.'
          }
        />
      ) : (
        <>
          <div className="table-scroll">
            <div className="table queue__table">
              <div className="table__head">
                <span>Nömrə</span>
                <span>Növ</span>
                <span>Məzmun</span>
                <span>Şikayətçi</span>
                <span>Yaş</span>
                <span>Prioritet</span>
              </div>

              {rows.map((row) => (
                <div
                  key={row.id}
                  className="table__row table__row--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => void navigate(`/novbe/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      void navigate(`/novbe/${row.id}`)
                    }
                  }}
                >
                  <span className="queue__code numeral">#{row.id}</span>
                  <span className="queue__reason">{row.reasonLabel}</span>
                  <span className="queue__excerpt" title={row.excerpt}>
                    “{row.excerpt}”
                  </span>
                  <span className="queue__reporter">@{row.reporterUsername}</span>
                  <span className="muted">{row.ageLabel}</span>
                  <span>
                    <span className={priorityClass(row.priority)}>{row.priorityLabel}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="row row--between queue__pager">
            <span className="muted">
              {number(page * PAGE_SIZE + 1)}–{number(page * PAGE_SIZE + rows.length)} /{' '}
              {number(total)} göstərilir
            </span>
            <div className="row">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Əvvəlki
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Növbəti
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
